import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Spinner } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { useTelemetry } from '@/telemetry';
import { ErrorState } from '@/components/ErrorState';
import { useAuth } from './AuthProvider';

interface ProtectedRouteProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function ProtectedRoute({ children, fallback }: ProtectedRouteProps): React.ReactElement {
  const { isAuthenticated, isLoading, login } = useAuth();
  const { trackEvent } = useTelemetry();
  const [authError, setAuthError] = useState<string | null>(null);
  const { t } = useTranslation('aita');
  // In MSAL mode login() starts a full-page loginRedirect whose promise never
  // resolves, so isAuthenticated/isLoading never change to stop this effect —
  // and StrictMode re-runs mount effects, which without a guard would fire a
  // second interactive redirect into MSAL's interaction_in_progress error and
  // surface a false auth failure. Reset only on real failure so retry works.
  const redirectStartedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated && !isLoading && !redirectStartedRef.current) {
      redirectStartedRef.current = true;
      // A fresh interactive sign-in starts at Partner Details rather than
      // resuming a browser-restored deep assessment path. Mid-session token
      // renewal (AuthProvider's acquireTokenRedirect) still resumes the current route.
      login('/app/details').catch((err: unknown) => {
        redirectStartedRef.current = false;
        const message = err instanceof Error ? err.message : t('auth.failed', 'Authentication failed');
        trackEvent('aita.auth.failure', { error: message, source: 'protected_route' });
        setAuthError(message);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t/trackEvent are only used for error reporting; re-running login() on every navigation would spam redirects
  }, [isAuthenticated, isLoading, login]);

  if (authError) {
    return (
      <ErrorState
        title={t('auth.failed', 'Authentication failed')}
        body={authError}
        onRetry={() => {
          redirectStartedRef.current = true;
          setAuthError(null);
          login('/app/details').catch((err: unknown) => {
            redirectStartedRef.current = false;
            setAuthError(err instanceof Error ? err.message : t('auth.failed', 'Authentication failed'));
          });
        }}
        retryLabel={t('entry.retry', 'Try again')}
      />
    );
  }

  if (isLoading) {
    return <Spinner label={t('auth.authenticating', 'Authenticating...')} />;
  }

  if (!isAuthenticated) {
    return fallback ? <>{fallback}</> : <Spinner label={t('auth.redirecting', 'Redirecting to sign in...')} />;
  }

  return <>{children}</>;
}
